import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { AuthedAvatar } from "@/attendee/AuthedAvatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Brain, Trophy, Lock, CheckCircle2, ChevronRight, ArrowLeft, Loader2,
  AlertTriangle, RefreshCw, Clock, Sparkles, Medal, Info, ListChecks,
} from "lucide-react";

const STATE_BADGE = {
  open: { label: "Open", cls: "bg-[hsl(var(--pc-gold)/0.18)] text-[hsl(var(--pc-gold))] border-[hsl(var(--pc-gold)/0.4)]" },
  scheduled: { label: "Opens soon", cls: "bg-[hsl(var(--pc-cream)/0.08)] text-cream-dim border-[hsl(var(--pc-line))]" },
  closed: { label: "Closed", cls: "bg-[hsl(var(--pc-cream)/0.08)] text-cream-dim border-[hsl(var(--pc-line))]" },
};

function HoldingState() {
  return (
    <div className="pt-16 px-4 pb-10" data-testid="quest-holding">
      <div className="pt-6 text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl grid place-items-center bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)] mb-4">
          <Brain className="h-8 w-8" />
        </div>
        <h1 className="font-serif text-3xl text-cream">PC Quest</h1>
        <p className="mt-2 text-sm text-cream-dim max-w-xs mx-auto">
          The competition hasn&apos;t opened yet. Check back soon &mdash; four categories and twenty
          questions await.
        </p>
      </div>
    </div>
  );
}

function ProgressBar({ value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-2 rounded-full bg-[hsl(var(--pc-cream)/0.1)] overflow-hidden" data-testid="quest-progress-bar">
      <div className="h-full rounded-full bg-[hsl(var(--pc-gold))] transition-[width] duration-300" style={{ width: `${pct}%` }} />
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Split "<short title> — <scenario>" into its two parts. The question bank stores
// a short title and the scenario joined by a spaced em dash; scoring is unaffected.
function splitPrompt(prompt) {
  if (!prompt) return { title: null, scenario: "" };
  const idx = prompt.indexOf(" \u2014 ");
  if (idx === -1) return { title: null, scenario: prompt };
  return { title: prompt.slice(0, idx).trim(), scenario: prompt.slice(idx + 3).trim() };
}

// Format a naive local opening datetime (already in the event/Los Cabos timezone)
// for display. Never invents a date — unset => "Opening date to be announced".
function fmtOpensLosCabos(s) {
  if (!s) return "Opening date to be announced";
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return "Opening date to be announced";
  const mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
  let h = parseInt(m[4], 10);
  const mins = m[5];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  const mmPart = mins === "00" ? "" : `:${mins}`;
  return `Opens ${MONTHS[mo - 1]} ${d} at ${h}${mmPart} ${ampm} \u00b7 Los Cabos time`;
}

function QuestionCard({ q, index, total, reveal, isTiebreak, onAnswer, onNext, onBackToCategories }) {
  const [selected, setSelected] = useState(q.selected_option_id || null);
  const [busy, setBusy] = useState(false);
  const answered = q.answered;
  const hasPos = typeof index === "number" && typeof total === "number";
  const isLast = hasPos && index === total - 1;
  const structured = !isTiebreak; // tie-break keeps its original (unchanged) layout
  const { title, scenario } = structured ? splitPrompt(q.prompt) : { title: null, scenario: q.prompt };

  const submit = async () => {
    if (!selected || answered || busy) return; // guard: no empty + no double submit while saving
    setBusy(true);
    const res = await onAnswer(q.question_id, selected);
    setBusy(false);
    if (!res) setSelected(q.selected_option_id || null);
  };

  return (
    <div
      className="att-card p-4"
      data-testid="quest-question"
      id={hasPos ? `quest-q-${index}` : undefined}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          {structured && hasPos && (
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--pc-gold))]" data-testid="quest-question-position">
              Question {index + 1} of {total} <span className="text-cream-dim">&middot;</span> {q.points || 100} points
            </div>
          )}
          {structured && title && (
            <div className="mt-1 font-serif text-base text-cream leading-snug" data-testid="quest-question-title">{title}</div>
          )}
        </div>
        {answered && !isTiebreak && typeof q.points_earned === "number" && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] px-2 py-0.5 text-[11px] font-bold" data-testid="quest-question-earned">
            +{q.points_earned}
          </span>
        )}
        {answered && isTiebreak && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] px-2 py-0.5 text-[11px] font-bold">
            <CheckCircle2 className="h-3 w-3" /> Saved
          </span>
        )}
      </div>

      <p className={`text-sm text-cream leading-snug mb-3 ${structured && title ? "" : "font-medium"}`} data-testid="quest-question-prompt">
        {scenario}
      </p>

      <div className="space-y-2" role="radiogroup">
        {q.options.map((o) => {
          const isSel = selected === o.id;
          const isCorrect = reveal && q.correct_option_id === o.id;
          const isMyWrong = reveal && q.selected_option_id === o.id && q.correct_option_id !== o.id;
          const interactive = !answered && !busy && !reveal;
          let cls = "border-[hsl(var(--pc-line))] text-cream";
          if (isCorrect) cls = "border-[hsl(142_45%_45%)] bg-[hsl(142_45%_30%/0.18)] text-cream";
          else if (isMyWrong) cls = "border-[hsl(0_60%_52%)] bg-[hsl(0_60%_40%/0.15)] text-cream";
          else if (isSel) cls = "border-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.12)] text-cream ring-1 ring-[hsl(var(--pc-gold)/0.45)]";
          // dot/ring colour for the selection indicator
          const dotBorder = isCorrect ? "border-[hsl(142_55%_55%)]"
            : isMyWrong ? "border-[hsl(0_60%_62%)]"
            : isSel ? "border-[hsl(var(--pc-gold))]"
            : "border-[hsl(var(--pc-cream)/0.4)]";
          const dotFill = isCorrect ? "bg-[hsl(142_55%_55%)]"
            : isMyWrong ? "bg-[hsl(0_60%_62%)]"
            : "bg-[hsl(var(--pc-gold))]";
          const showDot = isSel || isCorrect || isMyWrong;
          return (
            <button
              key={o.id}
              data-testid="quest-option"
              role="radio"
              aria-checked={isSel}
              disabled={answered || busy || reveal}
              onClick={() => interactive && setSelected(o.id)}
              className={`w-full text-left rounded-xl border px-3.5 py-2.5 text-sm transition-colors flex items-center gap-3 ${cls} ${interactive ? "hover:border-[hsl(var(--pc-cream)/0.35)] hover:bg-[hsl(var(--pc-cream)/0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold)/0.55)]" : ""} disabled:cursor-default`}
            >
              <span className={`shrink-0 grid place-items-center h-4 w-4 rounded-full border ${dotBorder}`} aria-hidden="true">
                {showDot && <span className={`h-2 w-2 rounded-full ${dotFill}`} />}
              </span>
              <span className="flex-1">{o.text}</span>
              {isCorrect && <CheckCircle2 className="h-4 w-4 text-[hsl(142_55%_55%)] shrink-0" />}
            </button>
          );
        })}
      </div>

      {reveal && q.explanation && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-[hsl(var(--pc-cream)/0.06)] p-3" data-testid="quest-explanation">
          <Info className="h-4 w-4 text-[hsl(var(--pc-gold))] shrink-0 mt-0.5" />
          <p className="text-xs text-cream-dim leading-snug">{q.explanation}</p>
        </div>
      )}

      {!answered && !reveal && (
        <Button onClick={submit} disabled={!selected || busy} data-testid="quest-submit-answer" className="w-full mt-3" size="sm">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} {busy ? "Saving\u2026" : "Submit answer"}
        </Button>
      )}

      {/* MAIN quest: updated saved copy + explicit next/back navigation */}
      {!isTiebreak && answered && !reveal && (
        <div className="mt-3 space-y-2.5">
          <p className="text-[11px] text-cream-dim flex items-start gap-1.5" data-testid="quest-answer-saved">
            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))] shrink-0 mt-0.5" />
            <span>Answer saved. Answers cannot be changed. Full answers and explanations unlock after the competition closes.</span>
          </p>
          {hasPos && (isLast ? (
            <Button variant="outline" size="sm" className="w-full" data-testid="quest-back-to-categories" onClick={() => onBackToCategories && onBackToCategories()}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to categories
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="w-full" data-testid="quest-next-question" onClick={() => onNext && onNext(index)}>
              Next question <ChevronRight className="h-4 w-4 ml-1.5" />
            </Button>
          ))}
        </div>
      )}

      {/* TIE-BREAK: concealment behaviour unchanged (no correctness / points shown) */}
      {isTiebreak && answered && !reveal && (
        <p className="mt-3 text-[11px] text-cream-dim flex items-center gap-1.5" data-testid="quest-answer-saved-tb">
          <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" /> Answer saved. Results are revealed after the tie-break closes.
        </p>
      )}
    </div>
  );
}

export default function Quest() {
  const { eventId, attendeeId } = useApp();
  const [phase, setPhase] = useState("loading"); // loading | holding | error | ok
  const [tab, setTab] = useState("quest"); // quest | leaderboard
  const [ov, setOv] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [catData, setCatData] = useState(null);
  const [catLoading, setCatLoading] = useState(false);
  const [lb, setLb] = useState(null);
  const [tb, setTb] = useState(null);          // tie-break overview (null if not assigned)
  const [tbView, setTbView] = useState(false);
  const [finalRes, setFinalRes] = useState(null);

  const loadOverview = useCallback(() => {
    if (!attendeeId) return;
    setPhase("loading");
    api.questVisible(eventId).then((v) => {
      if (!v.visible) { setPhase("holding"); return; }
      api.questOverview(eventId, attendeeId)
        .then((d) => { setOv(d); setPhase("ok"); })
        .catch(() => setPhase("error"));
      api.questTbOverview(eventId, attendeeId).then(setTb).catch(() => setTb(null));
      api.questFinalResults(eventId, attendeeId).then(setFinalRes).catch(() => setFinalRes(null));
    }).catch(() => setPhase("error"));
  }, [eventId, attendeeId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const openCategory = (cno) => {
    setActiveCat(cno);
    setCatLoading(true);
    api.questCategory(eventId, attendeeId, cno)
      .then((d) => setCatData(d))
      .catch(() => toast.error("Couldn't open that category."))
      .finally(() => setCatLoading(false));
  };

  const refreshCategory = () => activeCat && openCategory(activeCat);

  const scrollToQuestion = (fromIndex) => {
    const el = document.getElementById(`quest-q-${fromIndex + 1}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const backToCategories = () => { setActiveCat(null); setCatData(null); };

  const onAnswer = async (questionId, optionId) => {
    try {
      const res = await api.questAnswer(eventId, attendeeId, questionId, { selected_option_id: optionId });
      toast.success(res.points_earned > 0 ? `Correct! +${res.points_earned} points` : "Answer saved");
      // refresh category + overview totals
      refreshCategory();
      api.questOverview(eventId, attendeeId).then(setOv).catch(() => {});
      return res;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your answer.");
      return null;
    }
  };

  const loadLeaderboard = () => {
    api.questLeaderboard(eventId, attendeeId).then(setLb).catch(() => {});
  };

  useEffect(() => { if (tab === "leaderboard") loadLeaderboard(); }, [tab]);

  if (phase === "loading") {
    return <div className="pt-16 px-4" data-testid="quest-loading"><div className="h-7 w-40 att-card animate-pulse mb-4" /><div className="h-56 att-card animate-pulse" /></div>;
  }
  if (phase === "holding") return <HoldingState />;
  if (phase === "error") {
    return (
      <div className="pt-16 px-4">
        <div className="att-card p-6 text-center" data-testid="quest-error">
          <AlertTriangle className="h-6 w-6 text-[hsl(var(--pc-gold))] mx-auto mb-2" />
          <div className="font-serif text-lg text-cream">We couldn&apos;t load PC Quest</div>
          <button onClick={loadOverview} data-testid="quest-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))] px-4 py-1.5 text-xs font-semibold"><RefreshCw className="h-3.5 w-3.5" /> Retry</button>
        </div>
      </div>
    );
  }

  // Tie-break play view
  if (tbView && tb) {
    return <TiebreakView tb={tb} eventId={eventId} attendeeId={attendeeId}
      onBack={() => { setTbView(false); api.questTbOverview(eventId, attendeeId).then(setTb).catch(() => {}); }} />;
  }

  // Category detail view
  if (activeCat != null) {
    return (
      <div className="pt-16 px-4 pb-10" data-testid="quest-category-view">
        <button onClick={() => { setActiveCat(null); setCatData(null); }} data-testid="quest-category-back" className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]">
          <ArrowLeft className="h-3.5 w-3.5" /> All categories
        </button>
        {catLoading || !catData ? (
          <div className="h-56 att-card animate-pulse" />
        ) : (
          <>
            <div className="mb-4">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[hsl(var(--pc-gold))]">Category {catData.category_no}</span>
              <h1 className="font-serif text-2xl text-cream leading-tight">{catData.title}</h1>
              <p className="text-xs text-cream-dim mt-0.5">{catData.subtitle}</p>
            </div>
            {catData.state === "scheduled" ? (
              <div className="att-card p-6 text-center" data-testid="quest-category-locked">
                <Lock className="h-6 w-6 text-cream-dim mx-auto mb-2" />
                <p className="text-sm text-cream-dim">This round hasn&apos;t opened yet. Come back when it goes live.</p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]" data-testid="quest-category-detail-opens-at">
                  <Clock className="h-3.5 w-3.5" /> {fmtOpensLosCabos(catData.opens_at)}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {catData.reveal_answers && (
                  <div className="att-card p-3 flex items-center gap-2" data-testid="quest-reveal-banner">
                    <Sparkles className="h-4 w-4 text-[hsl(var(--pc-gold))]" />
                    <span className="text-xs text-cream">Results are in &mdash; correct answers and explanations are shown below.</span>
                  </div>
                )}
                {catData.questions.map((q, i) => (
                  <QuestionCard
                    key={q.question_id}
                    q={q}
                    index={i}
                    total={catData.questions.length}
                    reveal={catData.reveal_answers}
                    onAnswer={onAnswer}
                    onNext={scrollToQuestion}
                    onBackToCategories={backToCategories}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Overview + leaderboard
  return (
    <div className="pt-16 px-4 pb-10" data-testid="quest-overview">
      <div className="pt-2 pb-3">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Competition</span>
        <h1 className="font-serif text-3xl text-cream leading-tight">{ov.title}</h1>
        <p className="text-xs text-cream-dim mt-0.5">{ov.subtitle}</p>
      </div>

      {/* Score summary */}
      <div className="att-card p-4 mb-4">
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-cream-dim">My points</div>
            <div className="font-serif text-3xl text-[hsl(var(--pc-gold))]" data-testid="quest-my-points">{ov.my_points}<span className="text-base text-cream-dim"> / {ov.max_score}</span></div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-cream-dim">Answered</div>
            <div className="font-serif text-2xl text-cream" data-testid="quest-answered-count">{ov.answered}<span className="text-sm text-cream-dim"> / {ov.total_questions}</span></div>
          </div>
        </div>
        <ProgressBar value={ov.answered} max={ov.total_questions} />
        {ov.closed && <p className="mt-2 text-[11px] text-[hsl(var(--pc-gold))] font-semibold">The competition has closed. Final standings below.</p>}
      </div>

      {/* Final results banner */}
      {finalRes && finalRes.finalized && (
        <div className="att-card p-4 mb-4 border-[hsl(var(--pc-gold)/0.5)]" data-testid="quest-final-banner">
          <div className="flex items-center gap-2 text-[hsl(var(--pc-gold))] mb-2"><Trophy className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Winners announced</span></div>
          <ol className="space-y-1.5">
            {finalRes.winners.map((w) => (
              <li key={w.position} className="flex items-center gap-2.5" data-testid="quest-final-winner">
                <span className="w-6 text-center font-serif text-lg text-[hsl(var(--pc-gold))]">{w.position}</span>
                <AuthedAvatar photoUrl={w.photo_url} name={w.display_name} identityKey={w.attendee_id} className="h-8 w-8" />
                <div className="min-w-0"><div className="text-sm font-semibold text-cream truncate">{w.display_name}</div><div className="text-[11px] text-cream-dim truncate">{w.prize}</div></div>
              </li>
            ))}
          </ol>
          <div className="mt-2 pt-2 border-t border-[hsl(var(--pc-line))] text-xs text-cream" data-testid="quest-final-myresult">
            Your result: {finalRes.my_result.is_winner
              ? <span className="text-[hsl(var(--pc-gold))] font-semibold">Winner · #{finalRes.my_result.final_position} · {finalRes.my_result.prize}</span>
              : <span>{finalRes.my_result.excluded ? "Not eligible for prizes" : `#${finalRes.my_result.final_position || "—"} · ${finalRes.my_result.main_points} pts`}</span>}
          </div>
        </div>
      )}

      {/* Tie-break entry (assigned participants only) */}
      {tb && (
        <button onClick={() => setTbView(true)} data-testid="quest-tiebreak-entry"
          className="w-full att-card p-4 mb-4 flex items-center justify-between gap-3 border-[hsl(var(--pc-gold)/0.4)] hover:border-[hsl(var(--pc-gold)/0.7)]">
          <div className="text-left">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--pc-gold))]" /><span className="font-serif text-lg text-cream">Tie-break</span>
              <span className="text-[10px] rounded-full border px-2 py-0.5 font-semibold bg-[hsl(var(--pc-gold)/0.18)] text-[hsl(var(--pc-gold))] border-[hsl(var(--pc-gold)/0.4)]">{tb.finalized ? "Finalised" : tb.state === "open" ? "Open" : tb.state === "closed" ? "Closed" : "Opens soon"}</span>
            </div>
            <p className="text-[11px] text-cream-dim mt-0.5" data-testid="quest-tiebreak-subtext">{tb.finalized
              ? "Tie resolved \u00b7 Results finalised. Tap to view the tie-break (read-only)."
              : `You're in a tie — ${tb.answered}/${tb.total_questions} answered. Decides order only.`}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--pc-gold))] shrink-0" />
        </button>
      )}

      {/* Tabs */}
      <div className="grid grid-cols-2 gap-2 att-glass rounded-full p-1 mb-4">
        <button onClick={() => setTab("quest")} data-testid="quest-tab-quest" className={`rounded-full py-1.5 text-xs font-semibold transition-colors ${tab === "quest" ? "bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))]" : "text-cream"}`}>Categories</button>
        <button onClick={() => setTab("leaderboard")} data-testid="quest-tab-leaderboard" className={`rounded-full py-1.5 text-xs font-semibold transition-colors ${tab === "leaderboard" ? "bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))]" : "text-cream"}`}>Leaderboard</button>
      </div>

      {tab === "quest" ? (
        <>
          <div className="space-y-3">
            {ov.categories.map((c) => {
              const badge = STATE_BADGE[c.state] || STATE_BADGE.scheduled;
              const clickable = c.state === "open" || c.state === "closed";
              return (
                <button
                  key={c.category_no}
                  data-testid="quest-category-card"
                  disabled={!clickable}
                  onClick={() => clickable && openCategory(c.category_no)}
                  className={`w-full text-left att-card p-4 flex items-center justify-between gap-3 transition-colors ${clickable ? "hover:border-[hsl(var(--pc-gold)/0.5)]" : "opacity-70"}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-serif text-lg text-cream truncate">{c.title}</span>
                      <span className={`shrink-0 text-[10px] rounded-full border px-2 py-0.5 font-semibold ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <p className="text-[11px] text-cream-dim mt-0.5 truncate">{c.subtitle}</p>
                    {c.state === "scheduled" ? (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--pc-gold))]" data-testid="quest-category-opens-at">
                        <Clock className="h-3 w-3" /> {fmtOpensLosCabos(c.opens_at)}
                      </div>
                    ) : (
                      <div className="mt-1.5 text-[11px] text-cream-dim">
                        {c.answered}/{c.question_count} answered · <span className="text-[hsl(var(--pc-gold))] font-semibold">{c.points_earned} pts</span>
                      </div>
                    )}
                  </div>
                  {clickable ? <ChevronRight className="h-5 w-5 text-[hsl(var(--pc-gold))] shrink-0" /> : <Lock className="h-4 w-4 text-cream-dim shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Rules */}
          <div className="att-card p-4 mt-4" data-testid="quest-rules">
            <div className="flex items-center gap-2 text-[hsl(var(--pc-gold))] mb-2">
              <ListChecks className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">How it works</span>
            </div>
            <ul className="space-y-1.5">
              {ov.rules.map((r, i) => (
                <li key={i} className="text-xs text-cream-dim flex items-start gap-2"><span className="text-[hsl(var(--pc-gold))] mt-0.5">•</span><span>{r}</span></li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <Leaderboard lb={lb} eventId={eventId} attendeeId={attendeeId} />
      )}
    </div>
  );
}

function TiebreakView({ tb, eventId, attendeeId, onBack }) {
  const [data, setData] = useState(tb);
  const refresh = () => api.questTbOverview(eventId, attendeeId).then(setData).catch(() => {});

  const onAnswer = async (questionId, optionId) => {
    try {
      await api.questTbAnswer(eventId, attendeeId, questionId, { selected_option_id: optionId });
      toast.success("Answer saved");
      refresh();
      return { saved: true };
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your answer.");
      return null;
    }
  };

  return (
    <div className="pt-16 px-4 pb-10" data-testid="quest-tiebreak-view">
      <button onClick={onBack} data-testid="quest-tiebreak-back" className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Quest
      </button>
      <div className="mb-3">
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[hsl(var(--pc-gold))]">Tie-break</span>
        <h1 className="font-serif text-2xl text-cream leading-tight">Break the tie</h1>
      </div>
      {data.finalized && (
        <div className="att-card p-3 mb-3 flex items-center gap-2 border-[hsl(var(--pc-gold)/0.5)]" data-testid="quest-tiebreak-resolved">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--pc-gold))] shrink-0" />
          <span className="text-xs text-cream">Tie resolved &middot; Results finalised. This tie-break is shown for reference only &mdash; no further answers are needed.</span>
        </div>
      )}
      <div className="att-card p-4 mb-3" data-testid="quest-tiebreak-rules">
        <ul className="space-y-1.5">
          {data.rules.map((r, i) => <li key={i} className="text-xs text-cream-dim flex items-start gap-2"><span className="text-[hsl(var(--pc-gold))] mt-0.5">•</span><span>{r}</span></li>)}
        </ul>
      </div>
      {data.state === "scheduled" ? (
        <div className="att-card p-6 text-center" data-testid="quest-tiebreak-scheduled">
          <Clock className="h-6 w-6 text-cream-dim mx-auto mb-2" />
          <p className="text-sm text-cream-dim">The tie-break hasn't opened yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[11px] text-cream-dim px-1" data-testid="quest-tiebreak-progress">{data.answered}/{data.total_questions} answered</p>
          {data.reveal && typeof data.my_tiebreak_points === "number" && (
            <div className="att-card p-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--pc-gold))]" /><span className="text-xs text-cream">Tie-break closed — you scored {data.my_tiebreak_points}/{data.total_questions}.</span></div>
          )}
          {data.questions.map((q) => (
            <QuestionCard key={q.question_id} q={{ ...q, points_earned: null, points: 1 }} isTiebreak reveal={data.reveal} onAnswer={onAnswer} />
          ))}
        </div>
      )}
    </div>
  );
}

function Leaderboard({ lb, attendeeId }) {
  if (!lb) return <div className="h-56 att-card animate-pulse" data-testid="quest-leaderboard-loading" />;
  if (!lb.rows.length) {
    return (
      <div className="att-card p-6 text-center" data-testid="quest-leaderboard-empty">
        <Trophy className="h-6 w-6 text-cream-dim mx-auto mb-2" />
        <p className="text-sm text-cream-dim">No scores yet. Be the first on the board!</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="quest-leaderboard">
      {lb.finalized
        ? <p className="text-[11px] text-[hsl(var(--pc-gold))] font-semibold mb-1 px-1" data-testid="quest-leaderboard-final">Final results &mdash; winners confirmed.</p>
        : !lb.closed && <p className="text-[11px] text-cream-dim mb-1 px-1">Provisional standings &mdash; updates as people play.</p>}
      {lb.rows.map((r) => {
        const me = r.is_me;
        const medal = lb.finalized ? r.is_winner : (r.rank <= 3);
        return (
          <div
            key={r.attendee_id}
            data-testid="quest-leaderboard-row"
            className={`att-card p-3 flex items-center gap-3 ${me ? "border-[hsl(var(--pc-gold))] bg-[hsl(var(--pc-gold)/0.08)]" : ""}`}
          >
            <div className={`w-7 text-center font-serif text-lg ${medal ? "text-[hsl(var(--pc-gold))]" : "text-cream-dim"}`}>{r.rank}</div>
            <AuthedAvatar photoUrl={r.photo_url} name={r.display_name} identityKey={r.attendee_id} className="h-9 w-9" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-cream truncate">{r.display_name}{me && <span className="text-[hsl(var(--pc-gold))]"> · You</span>}</div>
            </div>
            <div className="font-serif text-lg text-[hsl(var(--pc-gold))]">{r.points}</div>
            {medal && <Medal className="h-4 w-4 text-[hsl(var(--pc-gold))]" />}
          </div>
        );
      })}
    </div>
  );
}
